import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { BookDashed, Plus } from "lucide-react"
import { DraftItem, useKnowledgeBaseForm } from "modules/knowledge/hooks/useKnowledgeBaseForm"
import { Loading } from "modules/shared/components/Loading"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { KnowledgeBaseItemKind } from "schema"
import { KnowledgeBaseItemForm } from "./ItemForm"

type KnowledgeBaseProps = {
  kind: keyof typeof KnowledgeBaseItemKind
  campaignId?: string
  highlightId?: string
}

export type KnowledgeBaseItemRef = {
  addItem: () => void
}

export const KnowledgeBaseItem = forwardRef<KnowledgeBaseItemRef, KnowledgeBaseProps>(
  ({ kind, campaignId, highlightId }, ref) => {
    const { t } = useTranslation()
    const {
      fields,
      isPending,
      onDelete,
      onUpdate,
      onAppend,
      onDuplicate,
      onRetrieveWebpage,
      updateItem,
    } = useKnowledgeBaseForm(kind, campaignId ?? null)
    const containerRef = useRef<HTMLDivElement>(null)
    const hasScrolledToHighlightRef = useRef(false)
    const [lastAddedId, setLastAddedId] = useState<string | null>(null)

    const { regularItems, campaignItems } = useMemo(() => {
      if (campaignId) {
        return { regularItems: fields, campaignItems: [] }
      }

      const regular: DraftItem[] = []
      const campaign: DraftItem[] = []

      fields.forEach((field) => {
        if (field.campaignId) {
          campaign.push(field)
        } else {
          regular.push(field)
        }
      })

      return { regularItems: regular, campaignItems: campaign }
    }, [fields, campaignId])

    useImperativeHandle(ref, () => ({
      addItem: () => {
        const draftId = onAppend()
        if (draftId) {
          setLastAddedId(draftId)
        }
        setTimeout(() => {
          containerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
        }, 0)
      },
    }))

    function onAddItem() {
      const draftId = onAppend()
      if (draftId) {
        setLastAddedId(draftId)
      }
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
      }, 0)
    }

    function renderItem(field: DraftItem, index: number) {
      const actualIndex = fields.findIndex((f) => f.id === field.id)
      return (
        <KnowledgeBaseItemForm
          key={field.originalDraftId ?? field.id}
          id={field.id || index.toString()}
          item={field}
          kind={kind}
          onRemove={() => onDelete(actualIndex)}
          isDisabled={!campaignId && !!field.campaignId}
          onUpdateField={(updates) => {
            if (field.id) updateItem(field.id, updates)
          }}
          onSave={(updates) => onUpdate(actualIndex, updates ? { updates } : {})}
          onDuplicate={() => onDuplicate(actualIndex)}
          campaignId={campaignId}
          onRetrieveWebpage={onRetrieveWebpage}
          autoFocus={lastAddedId === field.id}
          highlightId={highlightId}
        />
      )
    }

    useEffect(() => {
      if (!highlightId || hasScrolledToHighlightRef.current) return

      const timeout = setTimeout(() => {
        const element = containerRef.current?.querySelector<HTMLDivElement>(`#item-${highlightId}`)

        if (!element) return

        hasScrolledToHighlightRef.current = true
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 100)

      return () => clearTimeout(timeout)
    }, [regularItems, highlightId])

    return (
      <div className="flex flex-col gap-6">
        {isPending && fields.length === 0 ? (
          <div className="flex flex-col gap-4">
            <Loading />
          </div>
        ) : (
          <div className="flex flex-col gap-4" ref={containerRef}>
            {!campaignId && campaignItems.length > 0 && (
              <Accordion
                type="single"
                collapsible
                className="w-full border rounded-lg"
                value={highlightId ? "campaign-items" : undefined}
              >
                <AccordionItem value="campaign-items">
                  <AccordionTrigger>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-base font-medium">
                        {t("knowledge.form.campaignItems.title")}
                      </span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {t("knowledge.form.campaignItems.description")}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-4">
                    {campaignItems.map((field, index) => renderItem(field, index))}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            {regularItems.map((field, index) => renderItem(field, index))}
            {!isPending && fields.length === 0 && (
              <div className="flex flex-col gap-6 items-center justify-center py-10">
                <BookDashed className="h-16 w-16 text-muted-foreground" strokeWidth={1} />
                <div className="flex flex-col items-center text-center">
                  <p className="text-lg text-foreground">{t("knowledge.form.noEntries.title")}</p>
                  <p className="text-base text-muted-foreground">
                    {t("knowledge.form.noEntries.description")}
                  </p>
                </div>
              </div>
            )}
            {!isPending && fields.length > 2 && (
              <div className="flex justify-end">
                <Button type="button" onClick={onAddItem} className="shrink-0">
                  <Plus className="h-4 w-4" />
                  <span>{t("knowledge.form.addEntry")}</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
)

KnowledgeBaseItem.displayName = "KnowledgeBaseItem"
